import FillingSpinner from 'components/FillingSpinner'
import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormData, formDataDefault } from './types'
import Input from 'components/Form/Input'
import Button from 'components/Form/Button'
import '../../Report.scss'
import { useSelector } from 'react-redux'
import { RootState, store } from '../../../../store'
import CommonReport from '../../components/CommonReport'
import { reportActions } from '../../store/report-slice'
import CommonReportsService from '../../services/common-reports.service'
import { FETCH_REPORT_DELAY } from '../../utils/report.constants'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PickerValue } from '@mui/x-date-pickers/internals'
import InputTitle from '../../../../components/Form/InputTitle'
import SelectAsync from '../../../../components/Form/SelectAsync'
import VoohCampaignFormService from '../../../VoohCampaign/VoohCampaignForm/service'
import { VoohSourceMediaFormat } from '../../../VoohCampaign/VoohCampaignForm/types'

const VoohCampaignReport: React.FC = () => {
  const reportType = 'voohAvailability'
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>(formDataDefault)
  const { voohAvailability } = useSelector((state: RootState) => state.report)

  // We need it to fetch reports on page load
  useEffect(() => {
    void CommonReportsService.fetchReports(reportType)
  }, [])

  const generateReport = async () => {
    Promise.resolve(store.dispatch(reportActions.setLoading({ key: reportType, loading: true })))
      .then(() =>
        CommonReportsService.createVoohAvailabilityReport({
          brandId:
            formData.brandNetwork?.rawData?.type === VoohSourceMediaFormat.BRAND
              ? formData.brandNetwork.value
              : null,
          networkId:
            formData.brandNetwork?.rawData?.type === VoohSourceMediaFormat.NETWORK
              ? formData.brandNetwork.value
              : null,
          asIds: formData.asIds
            ? formData.asIds
                .split(/,| |;/)
                .map(val => {
                  return val.trim()
                })
                .filter(val => val)
            : null,
          startMonth: formData.startMonth || null,
          endMonth: formData.endMonth || null,
        })
      )
      .then(() => new Promise(resolve => setTimeout(resolve, FETCH_REPORT_DELAY)))
      .then(() => CommonReportsService.fetchReports(reportType))
      .finally(
        () => void store.dispatch(reportActions.setLoading({ key: reportType, loading: false }))
      )
  }

  const Form = (): ReactNode => (
    <div className='Report-vooh__form'>
      <SelectAsync
        id='brandNetwork'
        title={t('common.brandNetwork')}
        placeholder={t('common.brandNetwork')}
        value={formData.brandNetwork}
        isClearable={true}
        options={VoohCampaignFormService.getVoohBrandsNetworks}
        onChange={(clubMediaSource): void => {
          void setFormData({
            ...formData,
            brandNetwork: clubMediaSource,
          })
        }}
      />

      <Input
        id='asIds'
        title={t('report.mediaAsIds')}
        placeholder='89716, 91365, 91835'
        value={formData.asIds.toString()}
        onChange={(value: string) =>
          void setFormData({
            ...formData,
            asIds: value,
          })
        }
        disabled={voohAvailability.loading}
      />

      <div>
        <InputTitle
          title={t('common.month')}
          dataCy={`month-input-title`}
        />
        <DatePicker
          slotProps={{
            field: {
              clearable: true,
            },
          }}
          views={['month', 'year']}
          value={formData.startMonth ? new Date(formData.startMonth) : null}
          onChange={(pickerValue: PickerValue) => {
            const month = pickerValue ? new Date(pickerValue).toISOString() : null
            setFormData({ ...formData, startMonth: month })
          }}
        />
      </div>

      <div>
        <InputTitle
          title={t('common.month')}
          dataCy={`month-input-title`}
        />
        <DatePicker
          slotProps={{
            field: {
              clearable: true,
            },
          }}
          views={['month', 'year']}
          value={formData.endMonth ? new Date(formData.endMonth) : null}
          onChange={(pickerValue: PickerValue) => {
            const month = pickerValue ? new Date(pickerValue).toISOString() : null
            setFormData({ ...formData, endMonth: month })
          }}
        />
      </div>

      <div className='Report__generate-button-container'>
        <Button
          onClick={generateReport}
          disabled={voohAvailability.loading}
        >
          {t('report.generate')}
        </Button>
      </div>
    </div>
  )

  return (
    <>
      {Form()}
      {voohAvailability.loading ? (
        <FillingSpinner className='Report__loading' />
      ) : (
        CommonReport.ReportTable(voohAvailability.reports, t)
      )}
    </>
  )
}

export default VoohCampaignReport
